import PropTypes from "prop-types";
import React from "react";
import { toast } from "sonner";

import { Toaster } from "@/components/ui/sonner";

// Thin shim over `sonner`. The original implementation kept the
// active notification in React state and rendered a MUI Snackbar
// from the provider; every consumer reaches for
// `showNotification(severity, message)`. Sonner queues internally,
// so the reducer / dispatcher are no longer necessary — keep the
// same hook API and just translate severity to the right `toast.*`
// call.

const NotificationsContext = React.createContext(null);

const SEVERITY_HANDLERS = {
  success: toast.success,
  error: toast.error,
  warning: toast.warning,
  info: toast.info,
};

NotificationsProvider.propTypes = {
  children: PropTypes.any,
};

function NotificationsProvider({ children }) {
  // Stable identity so consumers that destructure `showNotification`
  // from the context don't re-render on every parent update.
  const value = React.useMemo(() => {
    const showNotification = (severity, message) => {
      const handler = SEVERITY_HANDLERS[severity] ?? toast;
      handler(message);
    };
    return { showNotification };
  }, []);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <Toaster position="bottom-right" />
    </NotificationsContext.Provider>
  );
}

function useNotificationsProvider() {
  const context = React.useContext(NotificationsContext);
  if (context === null) {
    throw new Error(
      "useNotificationsProvider must be used within a NotificationsProvider",
    );
  }
  return context;
}

export { NotificationsProvider, useNotificationsProvider };
